import { KPI_A, KPI_D, KPI_W, KPI_S, KPH_A, KPH_D, getOSM, globalItemDataStore } from '../OverworldAnimation.js';
import { moveDataLoader } from '../data/MoveDataLoader.js';
import { audioManager } from '../AudioManager.js';
import Pokemon from './Pokemon.js';
import {
    healingItemIDs,
    berryItemIds,
    vitaminItemIds,
    heldItemIds,
    pokeballItemIDs,
    moveItemIDs,
    hmIds,
    keyItemIds,
} from '../constants.js';

const PAGE_NAMES = ['General', 'Overworld Cheats', 'Wild Encounters', 'Battle Cheats'];
const OPTIONS_VISIBLE = 10; // Page selector + 9 content rows
const SCROLL_WINDOW_SIZE = 9; // Options below the page selector visible at once; page stays fixed

const MOVE_CATEGORY_VALUES = ['any', 'status', 'physical', 'special'];

// Bag-style item categories for Forced Item (same order as bag menu)
const ITEM_CATEGORY_OPTIONS = [
    { id: 'off', label: 'Off' },
    { id: 'items', label: 'Medicine' },
    { id: 'berries', label: 'Berries' },
    { id: 'vitamins', label: 'Vitamins' },
    { id: 'held_items', label: 'Held Items' },
    { id: 'pokeballs', label: 'Pokeballs' },
    { id: 'tms_hms', label: 'TMs & HMs' },
    { id: 'key_items', label: 'Key Items' },
];

const FORCE_LEVEL_OPTIONS = ['off', ...Array.from({ length: 100 }, (_, i) => String(i + 1))];

const TIME_OF_DAY_VALUES = ['day', 'dusk', 'night', 'dawn', 'variable'];
const TEXT_SPEED_VALUES = ['slow', 'medium', 'fast', 'super fast'];
const ON_OFF_VALUES = ['on', 'off'];
const GENDER_VALUES = ['off', 'boy', 'girl'];

export default class OptionsMenu {
    constructor() {
        this.currentPage = 0;
        this.currentOptionIndex = 0;

        // For accelerated value changes when holding keys
        this.horizontalHoldFrames = 0;
        this.currentHorizontalDirection = null;
        this.horizontalInitialDelay = 15; // Frames to wait before starting repeat
        this.horizontalRepeatRate = 2; // Frames between repeats (starts slow)
        this.horizontalMinRepeatRate = 1; // Fastest repeat rate (after acceleration)
        this.horizontalAccelerationFrames = 30; // Frames to reach max speed

        // General (page 0)
        this.timeOfDay = 'variable';
        this.dayNightCycleDuration = 900; // 15 minutes in seconds, 1-86400
        this.textSpeed = 'fast';
        this.soundVolume = 5; // 0-10
        this.autoSave = 'off';

        // Initialize audio volume
        audioManager.setVolume(this.soundVolume);

        // Overworld Cheats (page 1)
        this.overworldCheatWalkThroughWalls = 'off';
        this.overworldCheatSkipWildBattles = 'off';
        this.overworldCheatSkipTrainerBattles = 'off';
        this.overworldCheatSkipOverworldEvents = 'off';
        this.overworldCheatToggleGender = 'off';

        // Wild Encounters (page 2)
        this.overworldCheatForcedPokemon = 'off';
        this.overworldCheatForceSpecificLevel = 'off';
        this.overworldCheatForcedItemCategory = 'off';
        this.overworldCheatForcedItem = 'off';
        this.overworldCheatFoeMoveCategory = 'any';

        // Battle Cheats (page 3)
        this.battleCheatPlayerMoveAlwaysKO = 'off';
        this.battleCheatFoeMoveAlwaysKO = 'off';
        this.battleCheatPlayerMoveNeverMisses = 'off';
        this.battleCheatPlayerMoveAlwaysMisses = 'off';
        this.battleCheatOpponentMoveNeverMisses = 'off';
        this.battleCheatOpponentMoveAlwaysMisses = 'off';
        this.battleCheatPlayerMoveAlwaysCriticalHit = 'off';
        this.battleCheatFoeMoveAlwaysCriticalHit = 'off';
        this.battleCheatPokeballNeverFails = 'off';
        this.battleCheatAlwaysHighRoll = 'off';
        this.battleCheatMoveSlot0 = 'off';
        this.battleCheatMoveSlot1 = 'off';
        this.battleCheatMoveSlot2 = 'off';
        this.battleCheatMoveSlot3 = 'off';
    }

    serialize() {
        return {
            timeOfDay: this.timeOfDay,
            dayNightCycleDuration: this.dayNightCycleDuration,
            textSpeed: this.textSpeed,
            soundVolume: this.soundVolume,
            autoSave: this.autoSave,
            overworldCheatWalkThroughWalls: this.overworldCheatWalkThroughWalls,
            overworldCheatSkipWildBattles: this.overworldCheatSkipWildBattles,
            overworldCheatSkipTrainerBattles: this.overworldCheatSkipTrainerBattles,
            overworldCheatSkipOverworldEvents: this.overworldCheatSkipOverworldEvents,
            overworldCheatToggleGender: this.overworldCheatToggleGender,
            overworldCheatForcedPokemon: this.overworldCheatForcedPokemon,
            overworldCheatForceSpecificLevel: this.overworldCheatForceSpecificLevel,
            overworldCheatForcedItemCategory: this.overworldCheatForcedItemCategory,
            overworldCheatForcedItem: this.overworldCheatForcedItem,
            overworldCheatFoeMoveCategory: this.overworldCheatFoeMoveCategory,
            battleCheatPlayerMoveAlwaysKO: this.battleCheatPlayerMoveAlwaysKO,
            battleCheatFoeMoveAlwaysKO: this.battleCheatFoeMoveAlwaysKO,
            battleCheatPlayerMoveNeverMisses: this.battleCheatPlayerMoveNeverMisses,
            battleCheatPlayerMoveAlwaysMisses: this.battleCheatPlayerMoveAlwaysMisses,
            battleCheatOpponentMoveNeverMisses: this.battleCheatOpponentMoveNeverMisses,
            battleCheatOpponentMoveAlwaysMisses: this.battleCheatOpponentMoveAlwaysMisses,
            battleCheatPlayerMoveAlwaysCriticalHit: this.battleCheatPlayerMoveAlwaysCriticalHit,
            battleCheatFoeMoveAlwaysCriticalHit: this.battleCheatFoeMoveAlwaysCriticalHit,
            battleCheatPokeballNeverFails: this.battleCheatPokeballNeverFails,
            battleCheatAlwaysHighRoll: this.battleCheatAlwaysHighRoll,
            battleCheatMoveSlot0: this.battleCheatMoveSlot0,
            battleCheatMoveSlot1: this.battleCheatMoveSlot1,
            battleCheatMoveSlot2: this.battleCheatMoveSlot2,
            battleCheatMoveSlot3: this.battleCheatMoveSlot3,
        };
    }

    static deserialize(data) {
        const defaults = new OptionsMenu();
        if (!data || typeof data !== 'object') {
            return defaults.serialize();
        }
        // Migrate legacy: Force Specific Encounter on/off + Forced Pokemon, Force Specific Item on/off + Forced Item
        let forcedPokemon = data.overworldCheatForcedPokemon ?? defaults.overworldCheatForcedPokemon;
        const legacyEncounter = data.overworldCheatForceSpecificEncounter;
        if (legacyEncounter === 'off') {
            forcedPokemon = 'off';
        } else if (legacyEncounter === 'on' && data.overworldCheatForcedPokemon !== null && data.overworldCheatForcedPokemon !== undefined) {
            forcedPokemon = data.overworldCheatForcedPokemon;
        } else if (legacyEncounter !== null && legacyEncounter !== undefined && legacyEncounter !== 'on' && legacyEncounter !== 'off') {
            forcedPokemon = legacyEncounter;
        }
        let forcedItem = data.overworldCheatForcedItem ?? defaults.overworldCheatForcedItem;
        const legacyItemOn = data.overworldCheatForceSpecificItem;
        if (legacyItemOn === 'off') {
            forcedItem = 'off';
        } else if (legacyItemOn === 'on' && data.overworldCheatForcedItem !== null && data.overworldCheatForcedItem !== undefined) {
            forcedItem = data.overworldCheatForcedItem;
        }
        return {
            timeOfDay: data.timeOfDay ?? defaults.timeOfDay,
            dayNightCycleDuration: data.dayNightCycleDuration ?? defaults.dayNightCycleDuration,
            textSpeed: data.textSpeed ?? defaults.textSpeed,
            soundVolume: data.soundVolume ?? defaults.soundVolume,
            autoSave: data.autoSave ?? defaults.autoSave,
            overworldCheatWalkThroughWalls:
                data.overworldCheatWalkThroughWalls ?? defaults.overworldCheatWalkThroughWalls,
            overworldCheatSkipWildBattles:
                data.overworldCheatSkipWildBattles ?? defaults.overworldCheatSkipWildBattles,
            overworldCheatSkipTrainerBattles:
                data.overworldCheatSkipTrainerBattles ?? defaults.overworldCheatSkipTrainerBattles,
            overworldCheatSkipOverworldEvents:
                data.overworldCheatSkipOverworldEvents ?? defaults.overworldCheatSkipOverworldEvents,
            overworldCheatToggleGender:
                data.overworldCheatToggleGender ?? defaults.overworldCheatToggleGender,
            overworldCheatForcedPokemon: forcedPokemon,
            overworldCheatForceSpecificLevel:
                data.overworldCheatForceSpecificLevel ?? defaults.overworldCheatForceSpecificLevel,
            overworldCheatForcedItemCategory:
                data.overworldCheatForcedItemCategory ?? defaults.overworldCheatForcedItemCategory,
            overworldCheatForcedItem: forcedItem,
            overworldCheatFoeMoveCategory:
                data.overworldCheatFoeMoveCategory ?? defaults.overworldCheatFoeMoveCategory,
            battleCheatPlayerMoveAlwaysKO:
                data.battleCheatPlayerMoveAlwaysKO ?? defaults.battleCheatPlayerMoveAlwaysKO,
            battleCheatFoeMoveAlwaysKO:
                data.battleCheatFoeMoveAlwaysKO ?? defaults.battleCheatFoeMoveAlwaysKO,
            battleCheatPlayerMoveNeverMisses:
                data.battleCheatPlayerMoveNeverMisses ?? defaults.battleCheatPlayerMoveNeverMisses,
            battleCheatPlayerMoveAlwaysMisses:
                data.battleCheatPlayerMoveAlwaysMisses ?? defaults.battleCheatPlayerMoveAlwaysMisses,
            battleCheatOpponentMoveNeverMisses:
                data.battleCheatOpponentMoveNeverMisses ?? defaults.battleCheatOpponentMoveNeverMisses,
            battleCheatOpponentMoveAlwaysMisses:
                data.battleCheatOpponentMoveAlwaysMisses ?? defaults.battleCheatOpponentMoveAlwaysMisses,
            battleCheatPlayerMoveAlwaysCriticalHit:
                data.battleCheatPlayerMoveAlwaysCriticalHit ??
                defaults.battleCheatPlayerMoveAlwaysCriticalHit,
            battleCheatFoeMoveAlwaysCriticalHit:
                data.battleCheatFoeMoveAlwaysCriticalHit ?? defaults.battleCheatFoeMoveAlwaysCriticalHit,
            battleCheatPokeballNeverFails:
                data.battleCheatPokeballNeverFails ?? defaults.battleCheatPokeballNeverFails,
            battleCheatAlwaysHighRoll:
                data.battleCheatAlwaysHighRoll ?? defaults.battleCheatAlwaysHighRoll,
            battleCheatMoveSlot0: data.battleCheatMoveSlot0 ?? defaults.battleCheatMoveSlot0,
            battleCheatMoveSlot1: data.battleCheatMoveSlot1 ?? defaults.battleCheatMoveSlot1,
            battleCheatMoveSlot2: data.battleCheatMoveSlot2 ?? defaults.battleCheatMoveSlot2,
            battleCheatMoveSlot3: data.battleCheatMoveSlot3 ?? defaults.battleCheatMoveSlot3,
        };
    }

    applySerialized(data) {
        if (!data || typeof data !== 'object') return;
        const restored = OptionsMenu.deserialize(data);
        Object.keys(restored).forEach((key) => {
            this[key] = restored[key];
        });
        // Apply sound volume to audio manager when restoring from save
        if (restored.soundVolume !== undefined) {
            audioManager.setVolume(restored.soundVolume);
        }
    }

    /**
     * Returns region pokedex data for Force Specific Encounter: value options (off + sorted names) and nameToId map.
     * Uses localStorage 'pokedex_data'; first region key or 'boriken' fallback.
     */
    static getRegionPokedexEncounterData() {
        const fallback = { valueOptions: ['off'], nameToId: {} };
        try {
            const raw = localStorage.getItem('pokedex_data');
            if (!raw) return fallback;
            const pokedexData = JSON.parse(raw);
            const regionId = Object.keys(pokedexData)[0] || 'boriken';
            const region = pokedexData[regionId];
            if (!region || !Array.isArray(region) || region.length === 0) return fallback;
            const nameToId = {};
            const names = [];
            for (const entry of region) {
                const id = entry.pokemonId ?? entry.pokemon?.id ?? entry.id;
                if (id === null || id === undefined) continue;
                try {
                    const data = Pokemon.getRawPokemonDataV2(id);
                    if (data && data.name) {
                        nameToId[data.name] = typeof id === 'number' ? id : parseInt(id, 10);
                        names.push(data.name);
                    }
                } catch (_) {
                    // skip missing/invalid pokemon data
                }
            }
            names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
            return { valueOptions: ['off', ...names], nameToId };
        } catch (_) {
            return fallback;
        }
    }

    /**
     * Returns category options for Forced Item (same order as bag menu): valueOptions and categoryLabels.
     */
    static getItemCategoryOptions() {
        const valueOptions = ITEM_CATEGORY_OPTIONS.map((c) => c.id);
        const categoryLabels = {};
        ITEM_CATEGORY_OPTIONS.forEach((c) => {
            categoryLabels[c.id] = c.label;
        });
        return { valueOptions, categoryLabels };
    }

    /**
     * Returns item ids for a given category. When category is 'off', returns only ['off'].
     * Otherwise returns off + ids from that category that exist in globalItemDataStore, sorted by name.
     */
    static getItemOptionsForCategory(category) {
        const fallback = { valueOptions: ['off'], idToName: {} };
        if (!category || category === 'off') return fallback;
        const store = globalItemDataStore || {};
        let idList = [];
        switch (category) {
            case 'items':
                idList = [...(healingItemIDs || [])];
                break;
            case 'berries':
                idList = [...(berryItemIds || [])];
                break;
            case 'vitamins':
                idList = [...(vitaminItemIds || [])];
                break;
            case 'held_items':
                idList = [...(heldItemIds || [])];
                break;
            case 'pokeballs':
                idList = [...(pokeballItemIDs || [])];
                break;
            case 'tms_hms':
                idList = [...(moveItemIDs || []), ...(hmIds || [])];
                break;
            case 'key_items':
                idList = [...(keyItemIds || [])];
                break;
            default:
                return fallback;
        }
        const ids = idList.filter((id) => store[id]);
        if (ids.length === 0) return fallback;
        const idToName = {};
        ids.forEach((id) => {
            const item = store[id];
            idToName[id] = item?.name ?? id;
        });
        ids.sort((a, b) => (idToName[a] ?? a).localeCompare(idToName[b] ?? b, undefined, { sensitivity: 'base' }));
        return { valueOptions: ['off', ...ids], idToName };
    }

    static getMoveOptions() {
        const fallback = { valueOptions: ['off'], idToName: {} };
        try {
            const store = moveDataLoader?.store ?? {};
            const ids = Object.keys(store).filter((id) => id !== 'hit_self');
            ids.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
            const idToName = {};
            ids.forEach((id) => {
                const move = store[id];
                idToName[id] = move?.name ?? id;
            });
            return { valueOptions: ['off', ...ids], idToName };
        } catch (_) {
            return fallback;
        }
    }

    getPageOptionCount() {
        switch (this.currentPage) {
            case 0:
                return 5;
            case 1:
                return 5;
            case 2:
                return 5;
            case 3:
                return 14;
            default:
                return 0;
        }
    }

    /**
     * Scroll offset for the content list (options after the page selector).
     * Page selector (index 0) is always visible; rows 1-9 show a window of 9 content options.
     * The window scrolls upward when we reach the 5th item in the window (selection at row 4),
     * so the selected option stays at the 5th row when scrolling.
     */
    getScrollOffset() {
        const total = this.getCurrentPageOptions().length;
        const contentCount = total - 1; // options after page selector
        if (contentCount <= SCROLL_WINDOW_SIZE) return 0;
        if (this.currentOptionIndex <= 0) return 0;
        const maxScroll = contentCount - SCROLL_WINDOW_SIZE;
        const targetScroll = this.currentOptionIndex - 5; // keep selection at 5th item (index 4) in window
        return Math.max(0, Math.min(targetScroll, maxScroll));
    }

    /**
     * Returns 10 items: the page selector (always first) plus 9 content options from the scrolling window.
     */
    getVisibleOptions() {
        const all = this.getCurrentPageOptions();
        const scrollOffset = this.getScrollOffset();
        const page = all[0];
        const contentWindow = all.slice(1 + scrollOffset, 1 + scrollOffset + SCROLL_WINDOW_SIZE);
        return [page, ...contentWindow];
    }

    increaseVerticalOption() {
        const totalOptions = this.getCurrentPageOptions().length;
        if (this.currentOptionIndex < totalOptions - 1) {
            this.currentOptionIndex += 1;
        }
    }

    decreaseVerticalOption() {
        if (this.currentOptionIndex > 0) {
            this.currentOptionIndex -= 1;
        }
    }

    increasePage() {
        this.currentPage = (this.currentPage + 1) % 4;
        this.currentOptionIndex = 0;
    }

    decreasePage() {
        this.currentPage = (this.currentPage - 1 + 4) % 4;
        this.currentOptionIndex = 0;
    }

    cycleEnumForward(values, current) {
        const i = values.indexOf(current);
        return values[(i + 1) % values.length];
    }

    cycleEnumBackward(values, current) {
        const i = values.indexOf(current);
        return values[(i - 1 + values.length) % values.length];
    }

    increaseOptionValue(amount = 1) {
        if (this.currentOptionIndex === 0) return;
        const optionIndex = this.currentOptionIndex - 1;
        if (this.currentPage === 0) {
            switch (optionIndex) {
                case 0:
                    this.timeOfDay = this.cycleEnumForward(TIME_OF_DAY_VALUES, this.timeOfDay);
                    break;
                case 1: {
                    // Cycle duration: accelerate based on hold time
                    const cycleIncrement = Math.max(1, Math.floor(amount));
                    this.dayNightCycleDuration = Math.min(86400, this.dayNightCycleDuration + cycleIncrement);
                    break;
                }
                case 2:
                    this.textSpeed = this.cycleEnumForward(TEXT_SPEED_VALUES, this.textSpeed);
                    break;
                case 3:
                    this.soundVolume = Math.min(10, this.soundVolume + amount);
                    audioManager.setVolume(this.soundVolume);
                    break;
                case 4:
                    this.autoSave = this.cycleEnumForward(ON_OFF_VALUES, this.autoSave);
                    break;
            }
        } else if (this.currentPage === 1) {
            switch (optionIndex) {
                case 0:
                    this.overworldCheatWalkThroughWalls = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.overworldCheatWalkThroughWalls
                    );
                    break;
                case 1:
                    this.overworldCheatSkipWildBattles = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipWildBattles
                    );
                    break;
                case 2:
                    this.overworldCheatSkipTrainerBattles = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipTrainerBattles
                    );
                    break;
                case 3:
                    this.overworldCheatSkipOverworldEvents = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipOverworldEvents
                    );
                    break;
                case 4:
                    this.overworldCheatToggleGender = this.cycleEnumForward(
                        GENDER_VALUES,
                        this.overworldCheatToggleGender
                    );
                    if (this.overworldCheatToggleGender !== 'off') {
                        this.applyGenderToggle();
                    }
                    break;
            }
        } else if (this.currentPage === 2) {
            switch (optionIndex) {
                case 0: {
                    const encounterData = OptionsMenu.getRegionPokedexEncounterData();
                    this.overworldCheatForcedPokemon = this.cycleEnumForward(
                        encounterData.valueOptions,
                        this.overworldCheatForcedPokemon
                    );
                    break;
                }
                case 1:
                    this.overworldCheatForceSpecificLevel = this.cycleEnumForward(
                        FORCE_LEVEL_OPTIONS,
                        this.overworldCheatForceSpecificLevel
                    );
                    break;
                case 2: {
                    const catData = OptionsMenu.getItemCategoryOptions();
                    this.overworldCheatForcedItemCategory = this.cycleEnumForward(
                        catData.valueOptions,
                        this.overworldCheatForcedItemCategory
                    );
                    const itemData = OptionsMenu.getItemOptionsForCategory(this.overworldCheatForcedItemCategory);
                    if (!itemData.valueOptions.includes(this.overworldCheatForcedItem)) {
                        this.overworldCheatForcedItem = 'off';
                    }
                    break;
                }
                case 3: {
                    const itemData = OptionsMenu.getItemOptionsForCategory(this.overworldCheatForcedItemCategory);
                    this.overworldCheatForcedItem = this.cycleEnumForward(
                        itemData.valueOptions,
                        this.overworldCheatForcedItem
                    );
                    break;
                }
                case 4:
                    this.overworldCheatFoeMoveCategory = this.cycleEnumForward(
                        MOVE_CATEGORY_VALUES,
                        this.overworldCheatFoeMoveCategory
                    );
                    break;
            }
        } else if (this.currentPage === 3) {
            switch (optionIndex) {
                case 0: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot0 = this.cycleEnumForward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot0
                    );
                    break;
                }
                case 1: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot1 = this.cycleEnumForward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot1
                    );
                    break;
                }
                case 2: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot2 = this.cycleEnumForward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot2
                    );
                    break;
                }
                case 3: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot3 = this.cycleEnumForward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot3
                    );
                    break;
                }
                case 4:
                    this.battleCheatPlayerMoveAlwaysKO = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysKO
                    );
                    break;
                case 5:
                    this.battleCheatFoeMoveAlwaysKO = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatFoeMoveAlwaysKO
                    );
                    break;
                case 6:
                    this.battleCheatPlayerMoveNeverMisses = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveNeverMisses
                    );
                    break;
                case 7:
                    this.battleCheatPlayerMoveAlwaysMisses = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysMisses
                    );
                    break;
                case 8:
                    this.battleCheatOpponentMoveNeverMisses = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatOpponentMoveNeverMisses
                    );
                    break;
                case 9:
                    this.battleCheatOpponentMoveAlwaysMisses = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatOpponentMoveAlwaysMisses
                    );
                    break;
                case 10:
                    this.battleCheatPlayerMoveAlwaysCriticalHit = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysCriticalHit
                    );
                    break;
                case 11:
                    this.battleCheatFoeMoveAlwaysCriticalHit = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatFoeMoveAlwaysCriticalHit
                    );
                    break;
                case 12:
                    this.battleCheatPokeballNeverFails = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatPokeballNeverFails
                    );
                    break;
                case 13:
                    this.battleCheatAlwaysHighRoll = this.cycleEnumForward(
                        ON_OFF_VALUES,
                        this.battleCheatAlwaysHighRoll
                    );
                    break;
            }
        }
    }

    decreaseOptionValue(amount = 1) {
        if (this.currentOptionIndex === 0) return;
        const optionIndex = this.currentOptionIndex - 1;
        if (this.currentPage === 0) {
            switch (optionIndex) {
                case 0:
                    this.timeOfDay = this.cycleEnumBackward(TIME_OF_DAY_VALUES, this.timeOfDay);
                    break;
                case 1: {
                    // Cycle duration: accelerate based on hold time
                    const cycleDecrement = Math.max(1, Math.floor(amount));
                    this.dayNightCycleDuration = Math.max(1, this.dayNightCycleDuration - cycleDecrement);
                    break;
                }
                case 2:
                    this.textSpeed = this.cycleEnumBackward(TEXT_SPEED_VALUES, this.textSpeed);
                    break;
                case 3:
                    this.soundVolume = Math.max(0, this.soundVolume - amount);
                    audioManager.setVolume(this.soundVolume);
                    break;
                case 4:
                    this.autoSave = this.cycleEnumBackward(ON_OFF_VALUES, this.autoSave);
                    break;
            }
        } else if (this.currentPage === 1) {
            switch (optionIndex) {
                case 0:
                    this.overworldCheatWalkThroughWalls = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.overworldCheatWalkThroughWalls
                    );
                    break;
                case 1:
                    this.overworldCheatSkipWildBattles = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipWildBattles
                    );
                    break;
                case 2:
                    this.overworldCheatSkipTrainerBattles = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipTrainerBattles
                    );
                    break;
                case 3:
                    this.overworldCheatSkipOverworldEvents = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.overworldCheatSkipOverworldEvents
                    );
                    break;
                case 4:
                    this.overworldCheatToggleGender = this.cycleEnumBackward(
                        GENDER_VALUES,
                        this.overworldCheatToggleGender
                    );
                    if (this.overworldCheatToggleGender !== 'off') {
                        this.applyGenderToggle();
                    }
                    break;
            }
        } else if (this.currentPage === 2) {
            switch (optionIndex) {
                case 0: {
                    const encounterData = OptionsMenu.getRegionPokedexEncounterData();
                    this.overworldCheatForcedPokemon = this.cycleEnumBackward(
                        encounterData.valueOptions,
                        this.overworldCheatForcedPokemon
                    );
                    break;
                }
                case 1:
                    this.overworldCheatForceSpecificLevel = this.cycleEnumBackward(
                        FORCE_LEVEL_OPTIONS,
                        this.overworldCheatForceSpecificLevel
                    );
                    break;
                case 2: {
                    const catData = OptionsMenu.getItemCategoryOptions();
                    this.overworldCheatForcedItemCategory = this.cycleEnumBackward(
                        catData.valueOptions,
                        this.overworldCheatForcedItemCategory
                    );
                    const itemData = OptionsMenu.getItemOptionsForCategory(this.overworldCheatForcedItemCategory);
                    if (!itemData.valueOptions.includes(this.overworldCheatForcedItem)) {
                        this.overworldCheatForcedItem = 'off';
                    }
                    break;
                }
                case 3: {
                    const itemData = OptionsMenu.getItemOptionsForCategory(this.overworldCheatForcedItemCategory);
                    this.overworldCheatForcedItem = this.cycleEnumBackward(
                        itemData.valueOptions,
                        this.overworldCheatForcedItem
                    );
                    break;
                }
                case 4:
                    this.overworldCheatFoeMoveCategory = this.cycleEnumBackward(
                        MOVE_CATEGORY_VALUES,
                        this.overworldCheatFoeMoveCategory
                    );
                    break;
            }
        } else if (this.currentPage === 3) {
            switch (optionIndex) {
                case 0: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot0 = this.cycleEnumBackward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot0
                    );
                    break;
                }
                case 1: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot1 = this.cycleEnumBackward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot1
                    );
                    break;
                }
                case 2: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot2 = this.cycleEnumBackward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot2
                    );
                    break;
                }
                case 3: {
                    const moveData = OptionsMenu.getMoveOptions();
                    this.battleCheatMoveSlot3 = this.cycleEnumBackward(
                        moveData.valueOptions,
                        this.battleCheatMoveSlot3
                    );
                    break;
                }
                case 4:
                    this.battleCheatPlayerMoveAlwaysKO = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysKO
                    );
                    break;
                case 5:
                    this.battleCheatFoeMoveAlwaysKO = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatFoeMoveAlwaysKO
                    );
                    break;
                case 6:
                    this.battleCheatPlayerMoveNeverMisses = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveNeverMisses
                    );
                    break;
                case 7:
                    this.battleCheatPlayerMoveAlwaysMisses = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysMisses
                    );
                    break;
                case 8:
                    this.battleCheatOpponentMoveNeverMisses = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatOpponentMoveNeverMisses
                    );
                    break;
                case 9:
                    this.battleCheatOpponentMoveAlwaysMisses = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatOpponentMoveAlwaysMisses
                    );
                    break;
                case 10:
                    this.battleCheatPlayerMoveAlwaysCriticalHit = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatPlayerMoveAlwaysCriticalHit
                    );
                    break;
                case 11:
                    this.battleCheatFoeMoveAlwaysCriticalHit = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatFoeMoveAlwaysCriticalHit
                    );
                    break;
                case 12:
                    this.battleCheatPokeballNeverFails = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatPokeballNeverFails
                    );
                    break;
                case 13:
                    this.battleCheatAlwaysHighRoll = this.cycleEnumBackward(
                        ON_OFF_VALUES,
                        this.battleCheatAlwaysHighRoll
                    );
                    break;
            }
        }
    }

    manageInputs() {
        if (KPI_W) {
            this.decreaseVerticalOption();
        }
        if (KPI_S) {
            this.increaseVerticalOption();
        }
        if (this.currentOptionIndex === 0) {
            if (KPI_A) {
                this.decreasePage();
            }
            if (KPI_D) {
                this.increasePage();
            }
            // Reset horizontal hold state when on page selector
            this.currentHorizontalDirection = null;
            this.horizontalHoldFrames = 0;
        } else {
            const optionIndex = this.currentOptionIndex - 1;
            const isCycleDurationOption = this.currentPage === 0 && optionIndex === 1;

            if (KPI_A) {
                this.decreaseOptionValue(isCycleDurationOption ? 1 : 1);
                this.currentHorizontalDirection = 'left';
                this.horizontalHoldFrames = 0;
            } else if (KPH_A) {
                if (this.currentHorizontalDirection === 'left') {
                    this.horizontalHoldFrames++;
                    if (this.horizontalHoldFrames >= this.horizontalInitialDelay) {
                        const framesAfterDelay = this.horizontalHoldFrames - this.horizontalInitialDelay;
                        const accelerationFactor =
                            Math.min(this.horizontalAccelerationFrames, framesAfterDelay) /
                            this.horizontalAccelerationFrames;
                        const currentRepeatRate = Math.max(
                            this.horizontalMinRepeatRate,
                            Math.floor(this.horizontalRepeatRate * (1 - accelerationFactor * 0.8))
                        );
                        if (framesAfterDelay % currentRepeatRate === 0) {
                            const increment = isCycleDurationOption
                                ? Math.max(1, Math.floor(1 + accelerationFactor * 9))
                                : 1;
                            this.decreaseOptionValue(increment);
                        }
                    }
                }
            } else if (KPI_D) {
                this.increaseOptionValue(isCycleDurationOption ? 1 : 1);
                this.currentHorizontalDirection = 'right';
                this.horizontalHoldFrames = 0;
            } else if (KPH_D) {
                if (this.currentHorizontalDirection === 'right') {
                    this.horizontalHoldFrames++;
                    if (this.horizontalHoldFrames >= this.horizontalInitialDelay) {
                        const framesAfterDelay = this.horizontalHoldFrames - this.horizontalInitialDelay;
                        const accelerationFactor =
                            Math.min(this.horizontalAccelerationFrames, framesAfterDelay) /
                            this.horizontalAccelerationFrames;
                        const currentRepeatRate = Math.max(
                            this.horizontalMinRepeatRate,
                            Math.floor(this.horizontalRepeatRate * (1 - accelerationFactor * 0.8))
                        );
                        if (framesAfterDelay % currentRepeatRate === 0) {
                            const increment = isCycleDurationOption
                                ? Math.max(1, Math.floor(1 + accelerationFactor * 9))
                                : 1;
                            this.increaseOptionValue(increment);
                        }
                    }
                }
            }

            if (!KPH_A && !KPH_D && !KPI_A && !KPI_D) {
                this.currentHorizontalDirection = null;
                this.horizontalHoldFrames = 0;
            }
        }
        return false;
    }

    getCurrentPageName() {
        return PAGE_NAMES[this.currentPage];
    }

    /**
     * Apply gender toggle cheat by setting player gender based on cheat value.
     * Safely handles cases where player might not be initialized yet.
     */
    applyGenderToggle() {
        const osm = getOSM();
        if (osm && osm.player && this.overworldCheatToggleGender !== 'off') {
            osm.player.gender = this.overworldCheatToggleGender;
        }
    }

    getCurrentPageOptions() {
        const options = [];
        options.push({ name: 'Page', value: this.getCurrentPageName(), isPageSelector: true });

        if (this.currentPage === 0) {
            options.push({ name: 'Time Of Day', value: this.timeOfDay });
            options.push({ name: 'Day/Night Cycle Duration', value: `${this.dayNightCycleDuration}s` });
            options.push({ name: 'Text Speed', value: this.textSpeed });
            options.push({ name: 'Sound Volume', value: String(this.soundVolume) });
            options.push({ name: 'Auto-Save', value: this.autoSave });
        } else if (this.currentPage === 1) {
            options.push({ name: 'Walk Through Walls', value: this.overworldCheatWalkThroughWalls });
            options.push({ name: 'Skip Wild Battles', value: this.overworldCheatSkipWildBattles });
            options.push({ name: 'Skip Trainer Battles', value: this.overworldCheatSkipTrainerBattles });
            options.push({ name: 'Skip Overworld Events', value: this.overworldCheatSkipOverworldEvents });
            options.push({ name: 'Toggle Gender', value: this.overworldCheatToggleGender });
        } else if (this.currentPage === 2) {
            options.push({ name: 'Forced Pokemon', value: this.overworldCheatForcedPokemon });
            options.push({
                name: 'Forced Level',
                value: this.overworldCheatForceSpecificLevel,
            });
            const catData = OptionsMenu.getItemCategoryOptions();
            options.push({
                name: 'Forced Item Category',
                value: catData.categoryLabels[this.overworldCheatForcedItemCategory] ?? this.overworldCheatForcedItemCategory,
            });
            const itemData = OptionsMenu.getItemOptionsForCategory(this.overworldCheatForcedItemCategory);
            const forcedItemDisplay =
                this.overworldCheatForcedItemCategory === 'off'
                    ? 'Off'
                    : itemData.idToName[this.overworldCheatForcedItem] ?? this.overworldCheatForcedItem;
            options.push({ name: 'Forced Item', value: forcedItemDisplay });
            options.push({
                name: 'Move Category',
                value: this.overworldCheatFoeMoveCategory,
            });
        } else if (this.currentPage === 3) {
            const moveData = OptionsMenu.getMoveOptions();
            options.push({
                name: 'Move Slot 0',
                value: moveData.idToName[this.battleCheatMoveSlot0] ?? this.battleCheatMoveSlot0,
            });
            options.push({
                name: 'Move Slot 1',
                value: moveData.idToName[this.battleCheatMoveSlot1] ?? this.battleCheatMoveSlot1,
            });
            options.push({
                name: 'Move Slot 2',
                value: moveData.idToName[this.battleCheatMoveSlot2] ?? this.battleCheatMoveSlot2,
            });
            options.push({
                name: 'Move Slot 3',
                value: moveData.idToName[this.battleCheatMoveSlot3] ?? this.battleCheatMoveSlot3,
            });
            options.push({ name: 'Player Move Always K.O.', value: this.battleCheatPlayerMoveAlwaysKO });
            options.push({ name: 'Foe Move Always K.O.', value: this.battleCheatFoeMoveAlwaysKO });
            options.push({ name: 'Player Move Never Misses', value: this.battleCheatPlayerMoveNeverMisses });
            options.push({
                name: 'Player Move Always Misses',
                value: this.battleCheatPlayerMoveAlwaysMisses,
            });
            options.push({
                name: 'Opponent Move Never Misses',
                value: this.battleCheatOpponentMoveNeverMisses,
            });
            options.push({
                name: 'Opponent Move Always Misses',
                value: this.battleCheatOpponentMoveAlwaysMisses,
            });
            options.push({
                name: 'Player Move Always Critical Hit',
                value: this.battleCheatPlayerMoveAlwaysCriticalHit,
            });
            options.push({
                name: 'Foe Move Always Critical Hit',
                value: this.battleCheatFoeMoveAlwaysCriticalHit,
            });
            options.push({ name: 'Pokeball Never Fails', value: this.battleCheatPokeballNeverFails });
            options.push({ name: 'Always High Roll', value: this.battleCheatAlwaysHighRoll });
        }

        return options;
    }
}

